type PaymentFulfillmentResult = {
  paymentReceived: boolean;
  fulfillmentCompleted: boolean;
  fulfillmentFailed: boolean;
};

type CurrentPaymentResponse = {
  order?: {
    status?: string;
    fulfillmentStatus?: string;
  } | null;
};

const wait = (durationMs: number) => new Promise((resolve) => {
  setTimeout(resolve, durationMs);
});

export async function waitForPaymentFulfillment(
  invoiceNumber: string,
  attempts = 20,
  intervalMs = 1000,
): Promise<PaymentFulfillmentResult> {
  const apiFetch = useApiFetch();
  let result: PaymentFulfillmentResult = {
    paymentReceived: false,
    fulfillmentCompleted: false,
    fulfillmentFailed: false,
  };

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const data = await apiFetch<any>(`/api/payments/orders/${encodeURIComponent(invoiceNumber)}`);
    const order = data?.order ?? data;
    const paymentReceived = String(order?.status ?? "").toLowerCase() === "paid";
    const fulfillmentStatus = String(order?.fulfillmentStatus ?? "").toLowerCase();

    result = {
      paymentReceived,
      fulfillmentCompleted: paymentReceived && fulfillmentStatus === "completed",
      fulfillmentFailed: paymentReceived && fulfillmentStatus === "failed",
    };

    if (!paymentReceived || result.fulfillmentCompleted || result.fulfillmentFailed) {
      return result;
    }

    if (attempt < attempts - 1) {
      await wait(intervalMs);
    }
  }

  return result;
}

export async function waitForCurrentTenantPaymentFulfillment<T extends CurrentPaymentResponse>(
  loadCurrentPayment: () => Promise<T>,
  attempts = 20,
  intervalMs = 1000,
): Promise<{
  response: T;
  paymentReceived: boolean;
  fulfillmentCompleted: boolean;
  fulfillmentFailed: boolean;
}> {
  let response = await loadCurrentPayment();

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const order = response.order;
    if (!order) {
      return {
        response,
        paymentReceived: true,
        fulfillmentCompleted: true,
        fulfillmentFailed: false,
      };
    }

    const paymentReceived = String(order.status ?? "").toLowerCase() === "paid";
    const fulfillmentStatus = String(order.fulfillmentStatus ?? "").toLowerCase();
    const fulfillmentCompleted = paymentReceived && fulfillmentStatus === "completed";
    const fulfillmentFailed = paymentReceived && fulfillmentStatus === "failed";

    if (!paymentReceived || fulfillmentCompleted || fulfillmentFailed || attempt === attempts - 1) {
      return {
        response,
        paymentReceived,
        fulfillmentCompleted,
        fulfillmentFailed,
      };
    }

    await wait(intervalMs);
    response = await loadCurrentPayment();
  }

  return {
    response,
    paymentReceived: false,
    fulfillmentCompleted: false,
    fulfillmentFailed: false,
  };
}
